// Environment Variables Status Service
// Comprehensive check for all environment variables and integrations

import { logger } from '../utils/logger';

export interface EnvVarStatus {
  name: string;
  displayName: string;
  category: 'frontend' | 'backend' | 'admin';
  required: boolean;
  present: boolean;
  value?: string; // Masked value for display
  description: string;
}

export interface IntegrationStatus {
  name: string;
  displayName: string;
  status: 'configured' | 'partial' | 'not-configured' | 'error';
  message: string;
  requirements: EnvVarStatus[];
}

export class EnvironmentService {
  private static instance: EnvironmentService;
  
  private constructor() {}
  
  static getInstance(): EnvironmentService {
    if (!EnvironmentService.instance) {
      EnvironmentService.instance = new EnvironmentService();
    }
    return EnvironmentService.instance;
  }

  /**
   * Check all environment variables
   */
  checkAllEnvironmentVariables(): EnvVarStatus[] {
    const vars: EnvVarStatus[] = [
      // Frontend variables (VITE_ prefix)
      {
        name: 'VITE_GEMINI_API_KEY',
        displayName: 'Gemini API Key (Script)',
        category: 'frontend',
        required: true,
        present: this.isVarPresent(import.meta.env.VITE_GEMINI_API_KEY),
        value: this.maskValue(import.meta.env.VITE_GEMINI_API_KEY),
        description: '스크립트 생성용 Gemini API 키'
      },
      // Google Drive OAuth 기능 미사용 - 주석 처리
      // {
      //   name: 'VITE_GOOGLE_CLIENT_ID',
      //   displayName: 'Google OAuth Client ID',
      //   category: 'frontend',
      //   required: false,
      //   present: this.isVarPresent(import.meta.env.VITE_GOOGLE_CLIENT_ID),
      //   value: this.maskValue(import.meta.env.VITE_GOOGLE_CLIENT_ID),
      //   description: 'Google Drive 로그인용 OAuth ID'
      // },
      // {
      //   name: 'VITE_GOOGLE_API_KEY',
      //   displayName: 'Google API Key',
      //   category: 'frontend',
      //   required: false,
      //   present: this.isVarPresent(import.meta.env.VITE_GOOGLE_API_KEY),
      //   value: this.maskValue(import.meta.env.VITE_GOOGLE_API_KEY),
      //   description: 'Google Drive API 접근용'
      // },
      // {
      //   name: 'VITE_GOOGLE_DRIVE_FOLDER_ID',
      //   displayName: 'Google Drive Folder ID',
      //   category: 'frontend',
      //   required: false,
      //   present: import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID === '1FtfuWEo2VPZq5hhNAvulKWaSQVHZdMzs' || 
      //          this.isVarPresent(import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID),
      //   value: import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID === '1FtfuWEo2VPZq5hhNAvulKWaSQVHZdMzs' 
      //        ? '✅ 설정됨' 
      //        : this.maskValue(import.meta.env.VITE_GOOGLE_DRIVE_FOLDER_ID),
      //   description: '사용자 Drive 저장 폴더'
      // },
      {
        name: 'VITE_APPS_SCRIPT_URL',
        displayName: 'Apps Script Web App URL',
        category: 'frontend',
        required: false,
        present: this.isVarPresent(import.meta.env.VITE_APPS_SCRIPT_URL),
        value: this.maskValue(import.meta.env.VITE_APPS_SCRIPT_URL),
        description: '관리자 Drive 업로드용 Apps Script URL'
      },
      {
        name: 'VITE_APPS_SCRIPT_TOKEN',
        displayName: 'Apps Script Token',
        category: 'frontend',
        required: false,
        present: this.isVarPresent(import.meta.env.VITE_APPS_SCRIPT_TOKEN),
        value: this.maskValue(import.meta.env.VITE_APPS_SCRIPT_TOKEN),
        description: 'Apps Script 인증 토큰'
      }
    ];

    // Note: Backend variables cannot be checked from frontend
    // We'll check them via API health endpoints

    return vars;
  }

  /**
   * Check integration status
   */
  async checkIntegrations(): Promise<IntegrationStatus[]> {
    const integrations: IntegrationStatus[] = [];
    
    // 1. Script Generation (Gemini)
    const geminiVars = this.checkAllEnvironmentVariables().filter(v => 
      v.name === 'VITE_GEMINI_API_KEY'
    );
    integrations.push({
      name: 'script-generation',
      displayName: '📝 스크립트 생성',
      status: geminiVars.every(v => v.present) ? 'configured' : 'not-configured',
      message: geminiVars.every(v => v.present) 
        ? '✅ Gemini API 연결됨' 
        : '❌ Gemini API 키 필요',
      requirements: geminiVars
    });

    // 2. Google Drive (User) - OAuth 미사용으로 주석 처리
    // const driveVars = this.checkAllEnvironmentVariables().filter(v => 
    //   v.name.includes('GOOGLE') && v.category === 'frontend'
    // );
    // const driveConfigured = driveVars.filter(v => v.required).every(v => v.present);
    // const drivePartial = driveVars.some(v => v.present);
    
    // integrations.push({
    //   name: 'google-drive-user',
    //   displayName: '☁️ Google Drive (사용자)',
    //   status: driveConfigured ? 'configured' : drivePartial ? 'partial' : 'not-configured',
    //   message: driveConfigured 
    //     ? '✅ Drive 연동 가능' 
    //     : drivePartial
    //     ? '⚠️ 일부 설정 누락'
    //     : '❌ Drive 설정 필요',
    //   requirements: driveVars
    // });

    // 3. Video Generation (Backend)
    try {
      const videoHealthResp = await fetch('/api/video/health');
      const videoHealth = await videoHealthResp.json();
      
      integrations.push({
        name: 'video-generation',
        displayName: '🎬 비디오 생성',
        status: videoHealth.configured ? 'configured' : 'not-configured',
        message: videoHealth.configured 
          ? `✅ ${videoHealth.providers?.join(', ') || 'API'} 연결됨`
          : '❌ 비디오 API 설정 필요',
        requirements: []
      });
    } catch (error) {
      integrations.push({
        name: 'video-generation',
        displayName: '🎬 비디오 생성',
        status: 'error',
        message: '⚠️ 서버 연결 실패',
        requirements: []
      });
    }

    // 4. Notion Integration (Backend)
    try {
      const notionHealthResp = await fetch('/api/notion/health');
      const notionHealth = await notionHealthResp.json();
      
      integrations.push({
        name: 'notion',
        displayName: '📔 Notion 저장',
        status: notionHealth.notionReady ? 'configured' : 'not-configured',
        message: notionHealth.notionReady 
          ? '✅ Notion 연동됨'
          : `❌ Notion 설정 필요 (${notionHealth.missing?.join(', ') || 'API'})`,
        requirements: []
      });
    } catch (error) {
      integrations.push({
        name: 'notion',
        displayName: '📔 Notion 저장',
        status: 'error',
        message: '⚠️ 서버 연결 실패',
        requirements: []
      });
    }

    // 5. Admin Drive (Backend)
    try {
      const adminDriveResp = await fetch('/api/admin/health');
      const adminDrive = await adminDriveResp.json();
      
      integrations.push({
        name: 'admin-drive',
        displayName: '👤 관리자 Drive',
        status: adminDrive.configured ? 'configured' : 'not-configured',
        message: adminDrive.configured 
          ? '✅ 관리자 Drive 연동됨'
          : '❌ 관리자 Drive 미설정 (선택)',
        requirements: []
      });
    } catch (error) {
      // Admin Drive is optional, so no error state
      integrations.push({
        name: 'admin-drive',
        displayName: '👤 관리자 Drive',
        status: 'not-configured',
        message: '❌ 관리자 Drive 미설정 (선택)',
        requirements: []
      });
    }

    return integrations;
  }

  /**
   * Check if environment variable is present
   */
  private isVarPresent(value: any): boolean {
    // More lenient check - consider it present if it has any value
    if (value === undefined || value === null) {
      return false;
    }
    
    const str = String(value).trim();
    
    // Check for placeholder values
    if (str === 'undefined' || 
        str === 'null' || 
        str === '' ||
        str.startsWith('your_') ||
        str === 'YOUR_FOLDER_ID_HERE' ||
        str === 'YOUR_DEPLOYMENT_ID_HERE') {
      return false;
    }
    
    return true;
  }

  /**
   * Mask sensitive values for display
   */
  private maskValue(value: any): string | undefined {
    if (!this.isVarPresent(value)) {
      return undefined;
    }
    
    const str = String(value);
    if (str.length <= 8) {
      return '****';
    }
    
    // Show first 4 and last 4 characters
    return `${str.slice(0, 4)}...${str.slice(-4)}`;
  }

  /**
   * Get overall system health
   */
  async getSystemHealth(): Promise<{
    healthy: boolean;
    criticalErrors: string[];
    warnings: string[];
    summary: string;
  }> {
    const vars = this.checkAllEnvironmentVariables();
    const integrations = await this.checkIntegrations();
    
    const criticalErrors: string[] = [];
    const warnings: string[] = [];
    
    // Check required variables
    const missingRequired = vars.filter(v => v.required && !v.present);
    missingRequired.forEach(v => {
      criticalErrors.push(`필수 환경 변수 누락: ${v.displayName}`);
    });
    
    // Check critical integrations
    const scriptGen = integrations.find(i => i.name === 'script-generation');
    if (scriptGen?.status !== 'configured') {
      criticalErrors.push('스크립트 생성 API가 설정되지 않음');
    }
    
    // Check optional integrations
    const optionalIntegrations = integrations.filter(i => 
      i.name !== 'script-generation' && i.status !== 'configured'
    );
    optionalIntegrations.forEach(i => {
      if (i.status === 'error') {
        warnings.push(`${i.displayName} 연결 실패`);
      } else if (i.status === 'partial') {
        warnings.push(`${i.displayName} 일부 설정 누락`);
      }
    });
    
    const healthy = criticalErrors.length === 0;
    const summary = healthy 
      ? warnings.length === 0 
        ? '✅ 모든 시스템 정상 작동'
        : `⚠️ 시스템 작동 중 (${warnings.length}개 경고)`
      : `❌ 시스템 오류 (${criticalErrors.length}개 오류)`;
    
    return {
      healthy,
      criticalErrors,
      warnings,
      summary
    };
  }
}

// Export singleton instance
export const environmentService = EnvironmentService.getInstance();